import React, { FC, useState, useEffect, useRef, useReducer } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';

import Modal from '../../../../../componentLibrary/Modal';
import Tab from '../../../../../componentLibrary/Tab/index';
import FolderColumn from './FolderColumn';
import ImageColumn from './ImageColumn';
import UploadImageTab from './UploadImageTab';
import ImagePreviewColumn from './ImagePreviewColumn';
import * as Styled from './styles';
import BananatagButton from '../../../../../componentLibrary/BananatagButton';
import handleKeyPress from '../../../../../common/handleKeyPress';
import eventService from '../../../../../common/eventService';
import { request } from '../../../../../common/request';
import * as contentLibraryAction from '../../../../../actions/contentLibrary';
import * as contentLibraryActionTypes from '../../../../../constants/actionTypes/contentLibrary';
import * as modalActions from '../../../../../actions/modals';
import imageSources from '../../../../../constants/imageSources/imageSources';
import configs from '../../../../../constants/configs';
import rules from '../../../../../constants/businessRules/businessRules';
import { ERR_DELETE_CONTENT, ERR_MOVE_CONTENT } from '../../../../../constants/Errors';
import { TAppState } from '../../../../../reducers';
import { IImageContent, IImageLibraryModalProps, IOnSelect, IUploadImage } from '../../../../../types/imageLibrary';
import DeleteModal from './DeleteModal';
import FileStatuses from '../../../../../componentLibrary/UploadingImage/FileStatuses';

const MAX_IMAGE_FILE_NAME = 76;
const MODAL_WIDTH = 1030;
const MODAL_HEIGHT = 600;

interface FileStatus {
    file: File;
    errorMessage: string;
    isUploading: boolean;
}
type FileAction =
    | {
          type: 'ADD' | 'UPDATE';
          payload: FileStatus;
      }
    | {
          type: 'CLEAR';
      };
const filesReducer = (state: FileStatus[], action: FileAction) => {
    switch (action.type) {
        case 'ADD':
            return [...state, { ...action.payload, isUploading: true, errorMessage: '' }];
        case 'CLEAR':
            return [];
        case 'UPDATE':
            return state.map((fileStatus) => {
                // OS cannot have duplicate file names, so we can use the file name as a unique identifier
                if (fileStatus.file.name === action.payload.file.name) {
                    return {
                        ...fileStatus,
                        ...action.payload,
                    };
                }
                return fileStatus;
            });
        default:
            return state;
    }
};

const uploadImage = async ({
    event,
    dispatch,
    selectedFolderID,
    contentOffset,
    setContentOffset,
    dispatchFilesStatus,
    setShouldRenderFileStatuses,
}: IUploadImage) => {
    const files: { fileName: string; fileData: ArrayBuffer | string | null; originalFile: File }[] = [];
    const removedFiles: string[] = [];
    const imageFiles = event.target.files;
    const promises: Promise<any>[] = [];
    setShouldRenderFileStatuses(true);
    if (imageFiles) {
        for (let i = 0; i < imageFiles.length; i++) {
            const imageFile = imageFiles[i];
            const filePromise = new Promise<void>((resolve) => {
                const isGif = imageFile.type === 'image/gif';
                const sizeRule = isGif ? rules.MAX_GIF_UPLOAD_FILE_SIZE : rules.MAX_IMAGE_UPLOAD_FILE_SIZE;
                if (imageFile.size / 1024 / 1024 > sizeRule) {
                    removedFiles.push(imageFile.name);
                    resolve();
                } else {
                    dispatchFilesStatus({ type: 'ADD', payload: { file: imageFile } });
                    const reader = new window.FileReader();
                    reader.readAsDataURL(imageFile);
                    reader.onload = () => {
                        let imageFileName =
                            imageFile.name.length > MAX_IMAGE_FILE_NAME
                                ? imageFile.name.substring(0, MAX_IMAGE_FILE_NAME) +
                                  imageFile.name.substring(imageFile.name.length - 4)
                                : imageFile.name;

                        // Ensure the file extension is lowercase
                        const fileName = imageFileName.split('.');
                        imageFileName = `${fileName[0]}.${fileName[1].toLowerCase()}`;

                        files.push({ fileName: imageFileName, fileData: reader.result, originalFile: imageFile });
                        resolve();
                    };
                }
            });
            promises.push(filePromise);
        }
    }

    if (removedFiles.length > 0) {
        eventService.notify(
            `The following file(s) have exceeded the maximum limit of ${
                rules.MAX_GIF_UPLOAD_FILE_SIZE
            } MB for GIFs or ${rules.MAX_IMAGE_UPLOAD_FILE_SIZE}MB: ${removedFiles.join()}`,
            true
        );
    }

    const uploadedImages: IImageContent[] = [];
    await Promise.all(promises);
    if (files.length > 0) {
        await Promise.all(
            files.map(({ fileData, fileName, originalFile }) =>
                request
                    .post(configs.contentLibraryEndpoint, {
                        file: {
                            fileData,
                            fileName,
                        },
                        folderID: selectedFolderID,
                    })
                    .then((newImage) => {
                        uploadedImages.push(newImage);
                        dispatchFilesStatus({
                            type: 'UPDATE',
                            payload: { file: originalFile, isUploading: false },
                        });
                    })
                    .catch((err) => {
                        dispatchFilesStatus({
                            type: 'UPDATE',
                            payload: { file: originalFile, isUploading: false, errorMessage: err },
                        });
                    })
            )
        );
    }
    setContentOffset(contentOffset + uploadedImages.length);
    dispatch(
        contentLibraryAction.updateFolderContent({
            content: uploadedImages,
            method: contentLibraryActionTypes.UPDATE_CONTENT_INSERTION_METHOD.PREPEND,
        })
    );
};

const ImageLibraryModal: FC<IImageLibraryModalProps> = ({
    selectedElementData,
    contents,
    folders,
    selectedFolder,
    dispatch,
    canManageLibrary,
    isLoading,
    modalTab,
}) => {
    const [activeTab, setActiveTab] = useState(modalTab);
    const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
    const [selectedImages, setSelectedImages] = useState<IImageContent[]>([]);
    const [contentOffset, setContentOffset] = useState(rules.IMAGE_LIBRARY_LOAD_BATCH_SIZE);
    const [dropDownState, setDropDownState] = useState(false);
    const [shouldRenderFileStatuses, setShouldRenderFileStatuses] = useState(false);
    const [filesStatus, dispatchFilesStatus] = useReducer(filesReducer, []);
    const areImagesSelected = selectedImages.length > 0;
    const displayPreview = selectedImages.length === 1;

    const Tabs = [
        { id: 1, label: 'Upload to My Draft' },
        { id: 2, label: 'Shared Library' },
    ];

    const setCurrentTab = (tab: number) => {
        if (tab === 2) {
            dispatch(contentLibraryAction.setFolders(2));
        }
        setActiveTab(tab);
    };

    const onSelect = ({ e, content, isCheckbox }: IOnSelect) => {
        e.stopPropagation();
        const isSelected = selectedImages.some((image) => image.contentID === content.contentID);
        if (e.ctrlKey || e.metaKey || isCheckbox) {
            if (isSelected) {
                setSelectedImages((selection) => selection.filter((image) => image.contentID !== content.contentID));
            } else {
                setSelectedImages((selection) => [...selection, content]);
            }
        } else {
            setSelectedImages([content]);
        }
    };

    const handleDelete = async () => {
        const deletedImages = selectedImages.map(({ contentID, contentName }) => ({ contentID, contentName }));
        try {
            setShowDeleteWarningModal(false);
            dispatch(contentLibraryAction.setLoading(true));
            await request.custom(configs.contentLibraryEndpoint, {
                method: 'DELETE',
                body: JSON.stringify({
                    contents: deletedImages,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            dispatch(contentLibraryAction.deleteFolderContent(selectedImages));
            dispatch(contentLibraryAction.updateContent(selectedImages.length, contentOffset - selectedImages.length));
            eventService.notify('The selected images have been deleted.');
        } catch (err) {
            eventService.notify(err.customMessage ? err.customMessage : ERR_DELETE_CONTENT, true);
        } finally {
            dispatch(contentLibraryAction.setLoading(false));
            setSelectedImages([]);
        }
    };

    const selectToggle = () => {
        if (areImagesSelected) {
            setSelectedImages([]);
        } else {
            setSelectedImages(contents);
        }
    };

    const hideImageLibrary = () => {
        dispatch(modalActions.setModal({ name: 'contentLibraryModal', status: false, location: null }));
    };

    const handleMoveToFolder = async (destinationFolderID: number) => {
        const imageToBeMoved = selectedImages.map(({ contentID }) => contentID);
        try {
            dispatch(contentLibraryAction.setLoading(true));
            await request.put(configs.contentLibraryEndpoint, {
                contents: imageToBeMoved,
                folderID: destinationFolderID,
            });
            dispatch(contentLibraryAction.deleteFolderContent(selectedImages));
            dispatch(contentLibraryAction.updateContent(selectedImages.length, contentOffset - selectedImages.length));
            eventService.notify('The selected images have been moved.');
        } catch (err) {
            eventService.notify(err.customMessage ? err.customMessage : ERR_MOVE_CONTENT, true);
        } finally {
            dispatch(contentLibraryAction.setLoading(false));
            setSelectedImages([]);
            setDropDownState(false);
        }
    };

    const closeModalButton = useRef(null);

    useEffect(() => {
        closeModalButton.current.focus();
    }, []);

    return (
        <Modal
            width={MODAL_WIDTH}
            height={MODAL_HEIGHT}
            backgroundColor="#F7F9FA"
            dataTestId="image-library-modal"
            onEsc={hideImageLibrary}
        >
            <Styled.CloseButton
                role="button"
                aria-label="Close image library"
                onClick={hideImageLibrary}
                onKeyDown={(e) => handleKeyPress(e, hideImageLibrary)}
                tabIndex={0}
                ref={closeModalButton}
            >
                <Styled.Icon icon="times" />
            </Styled.CloseButton>
            <Styled.ModalDiv isLoading={isLoading}>
                <Styled.ModalTabsDiv>
                    {Tabs.map((tab) => (
                        <Tab
                            key={tab.id}
                            onClick={() => setCurrentTab(tab.id)}
                            label={tab.label}
                            isActive={activeTab === tab.id}
                        />
                    ))}
                </Styled.ModalTabsDiv>
                {activeTab === 1 && (
                    <UploadImageTab selectedElementData={selectedElementData} hideImageLibrary={hideImageLibrary} />
                )}
                {activeTab === 2 && (
                    <>
                        {isLoading && (
                            <Styled.LoaderDiv>
                                <Styled.Loader src={imageSources.CONTENT_LIBRARY_LOADER} alt="Loading your content" />
                            </Styled.LoaderDiv>
                        )}
                        <Styled.TopDiv onMouseLeave={() => setDropDownState(false)}>
                            {showDeleteWarningModal && (
                                <DeleteModal
                                    modalText="If you delete a file from the library, it will NOT break the drafts/templates that use the file."
                                    toggleDeleteModal={() => setShowDeleteWarningModal(!showDeleteWarningModal)}
                                    onDelete={handleDelete}
                                />
                            )}
                            {/* TODO: IMPLEMENT SEARCH FOR RELEASE 2 */}
                            {/* <Input
                                type="text"
                                width={462}
                                placeholder="Search library"
                                icon="search"
                                styling={{
                                    backgroundColor: 'white',
                                    alignSelf: 'flex-start',
                                }}
                                onChange={() => { }}
                            /> */}
                            {canManageLibrary && contents.length > 0 && (
                                <BananatagButton
                                    onClick={selectToggle}
                                    size="small"
                                    text={areImagesSelected ? 'deselect all' : 'select all'}
                                    ariaLabel={`${areImagesSelected ? 'deselect' : 'select'} all images`}
                                    type="outline"
                                    styling={{ marginRight: 8 }}
                                />
                            )}
                            {canManageLibrary && areImagesSelected && (
                                <BananatagButton
                                    onClick={() => setDropDownState(!dropDownState)}
                                    size="small"
                                    text="Move to..."
                                    ariaLabel="Move images to"
                                    type="outline"
                                    styling={{ marginRight: 8 }}
                                />
                            )}
                            {dropDownState && (
                                <Styled.DropDown onMouseLeave={() => setDropDownState(false)}>
                                    {folders
                                        .filter((folder) => folder.folderID !== selectedFolder.id)
                                        .map((folder) => (
                                            <Styled.DropDownItem
                                                onClick={() => handleMoveToFolder(folder.folderID)}
                                                onKeyDown={(e) =>
                                                    handleKeyPress(e, () => handleMoveToFolder(folder.folderID))
                                                }
                                                tabIndex={0}
                                            >
                                                {folder.folderName}
                                            </Styled.DropDownItem>
                                        ))}
                                </Styled.DropDown>
                            )}
                            {canManageLibrary && areImagesSelected && (
                                <BananatagButton
                                    size="small"
                                    text="delete"
                                    ariaLabel="Delete images"
                                    type="warning"
                                    onClick={() => setShowDeleteWarningModal(true)}
                                />
                            )}
                            {canManageLibrary && !areImagesSelected && (
                                <BananatagButton
                                    file
                                    size="small"
                                    index="image-upload-to-folder"
                                    text="UPLOAD TO FOLDER"
                                    ariaLabel="Upload image to folder"
                                    multiple
                                    allowGifs
                                    onChange={(event) => {
                                        uploadImage({
                                            event,
                                            dispatch,
                                            selectedFolderID: selectedFolder.id,
                                            contentOffset,
                                            setContentOffset,
                                            dispatchFilesStatus,
                                            setShouldRenderFileStatuses,
                                            // eslint-disable-next-line no-console
                                        }).catch((err) => console.error(`Error uploading image. ${err}`));
                                        event.target.value = null;
                                    }}
                                />
                            )}
                        </Styled.TopDiv>

                        <Styled.ModalGrid displayPreview={displayPreview}>
                            <FolderColumn
                                canManageLibrary={canManageLibrary}
                                deselectAll={() => setSelectedImages([])}
                                folders={folders}
                            />
                            <ImageColumn
                                canManageLibrary={canManageLibrary}
                                contents={contents}
                                displayPreview={displayPreview}
                                isLoading={isLoading}
                                onSelect={onSelect}
                                selectedImages={selectedImages}
                                selectedFolderID={selectedFolder.id}
                                dispatch={dispatch}
                                contentOffset={contentOffset}
                                setContentOffset={setContentOffset}
                            />
                            <Styled.UploadingImageBackground shouldRender={shouldRenderFileStatuses}>
                                <FileStatuses
                                    files={filesStatus}
                                    close={() => {
                                        dispatchFilesStatus({ type: 'CLEAR' });
                                        setShouldRenderFileStatuses(false);
                                    }}
                                />
                            </Styled.UploadingImageBackground>
                            {displayPreview && (
                                <ImagePreviewColumn
                                    canManageLibrary={canManageLibrary}
                                    content={selectedImages[0]}
                                    hideImageLibrary={hideImageLibrary}
                                    selectedElementData={selectedElementData}
                                />
                            )}
                        </Styled.ModalGrid>
                        <Styled.HelpCenterLink>
                            <Styled.Icon color="true" icon="question-circle" />
                            <Styled.Link
                                href="https://support.staffbase.com/hc/en-us/articles/4412584512914"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Learn more about the Image Library in our Help Center.
                            </Styled.Link>
                        </Styled.HelpCenterLink>
                    </>
                )}
            </Styled.ModalDiv>
        </Modal>
    );
};

const mapStateToProps = (state: TAppState) => {
    const isAdminOrParentAdmin = _get(state, 'session.user.isAdminOrParentAdmin', 'BASE_USER');
    const integration = _get(state, 'session.integration', '');
    const canManageLibrary = isAdminOrParentAdmin && (integration === 'APP' || integration === 'LOCALHOST');
    return {
        contents: _get(state, 'contentLibrary.selectedFolder.contents', []),
        folders: _get(state, 'contentLibrary.folders', []),
        selectedFolder: _get(state, 'contentLibrary.selectedFolder', ''),
        integration,
        isLoading: _get(state, 'contentLibrary.isLoading', true),
        modalTab: _get(state, 'modals.contentLibraryModal.location', ''),
        canManageLibrary,
    };
};

export default connect(mapStateToProps)(ImageLibraryModal);
